import { useMemo, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { communityApi, type Pick, type PlanTouchInput } from '@/modules/community/api/communityApi';
import { communityKeys } from '@/modules/community/queryKeys';
import { PlanTouchModal, type PlanTarget } from '@/modules/community/components/PlanTouchModal';
import { ReservePicks } from '@/modules/community/components/ReservePicks';
import { fallbackTake, matchesFilter, pickFilters, pickInitials } from '../utils/joePicks';
import { JoePickRow } from './JoePickRow';
import styles from './JoeWeekPicks.module.css';

/**
 * Joe's monthly picks task, read live from the Community module instead of the report's frozen text.
 * It reads the same query the Community page reads and acts through the same endpoints, so a pick
 * snoozed, passed on or planned in either place shows the same way in both. Joe is not involved:
 * nothing here writes to the task or asks him for a new version.
 *
 * Laid out as Joe's take (a short summary and three counts), then (1) the picks as a decision list
 * with progress, then (2) his other options, folded and marked optional. Joe's report text shows as
 * the take when it is short; a long one, which lists every pick again, is folded behind "Read Joe's
 * full note" and the take is written from the picks.
 */

const STATE_LABEL: Record<string, string> = {
  planned: 'Touch planned',
  snoozed: 'Snoozed 30 days',
  dismissed: 'Not for me',
};

const STATE_CLASS: Record<string, string> = {
  planned: styles.statePlanned,
  snoozed: styles.stateSnoozed,
  dismissed: styles.stateDismissed,
};

export function JoeWeekPicks({ locationId, take, fullNote }: { locationId: string; take: string | null; fullNote: ReactNode | null }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState('all');
  const [noteOpen, setNoteOpen] = useState(false);
  const [target, setTarget] = useState<PlanTarget | null>(null);
  const { data: state } = useQuery({
    queryKey: communityKeys.state(locationId),
    queryFn: () => communityApi.getState(locationId),
    enabled: !!locationId,
  });
  const { data: picks, isLoading } = useQuery({
    queryKey: communityKeys.picks(locationId),
    queryFn: () => communityApi.getPicks(locationId),
    enabled: !!locationId,
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: communityKeys.picks(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.network(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.partners(locationId) });
  };
  const snooze = useMutation({
    mutationFn: (pickId: string) => communityApi.snoozePick(locationId, pickId),
    onSuccess: (r) => {
      toast.success(r.message);
      refresh();
    },
    onError: (err: Error) => toast.error(err.message || 'Could not snooze the pick'),
  });
  const addReserve = useMutation({
    mutationFn: (pickId: string) => communityApi.addReservePick(locationId, pickId),
    onSuccess: (r) => {
      toast.success(r.message);
      refresh();
    },
    onError: (err: Error) => toast.error(err.message || 'Could not add the pick'),
  });
  const dismiss = useMutation({
    mutationFn: ({ pickId, reason }: { pickId: string; reason: string }) => communityApi.dismissPick(locationId, pickId, reason),
    onSuccess: (r) => {
      toast.success(r.followUp ? `${r.message} Joe has a follow-up question for you on the Community page.` : r.message);
      refresh();
    },
    onError: (err: Error) => toast.error(err.message || 'Could not pass on the pick'),
  });
  const plan = useMutation({
    mutationFn: (input: PlanTouchInput) => communityApi.planTouch(locationId, input),
    onSuccess: (r) => {
      setTarget(null);
      toast.success(r.draftRequested ? 'Planned. Joe is drafting the words; the draft will show in Tasks.' : 'Planned. It is in your network now.');
      refresh();
    },
    onError: (err: Error) => toast.error(err.message || 'Could not save the touch'),
  });

  const week = useMemo(() => picks?.weekPicks ?? [], [picks]);
  // Open means on the page's stream: this month's new picks and the snoozes that came back.
  const open = useMemo(() => {
    const streamIds = new Set((picks?.picks ?? []).map((p) => p.id));
    return week.filter((p) => streamIds.has(p.id));
  }, [picks, week]);
  const acted = useMemo(() => week.filter((p) => !open.includes(p)), [week, open]);
  const filters = useMemo(() => pickFilters(open), [open]);

  if (isLoading || !picks) return <Skeleton height={160} />;
  const reserve = picks.reserve ?? [];
  const busy = snooze.isPending || dismiss.isPending || addReserve.isPending || plan.isPending;
  const activeFilter = filters.some((f) => f.key === filter) ? filter : 'all';
  const shown = open.filter((p) => matchesFilter(p, activeFilter));
  const newOpen = open.filter((p) => p.isNew).length;
  const decidedPct = week.length === 0 ? 0 : Math.round((acted.length / week.length) * 100);
  const radius = state?.settings.radiusMi;

  return (
    <div className={styles.panel}>
      <section className={styles.take} aria-label="Joe's take">
        <div className={styles.takeHead}>
          <span className={styles.joeAvatar} aria-hidden="true">
            J
          </span>
          <div className={styles.takeBody}>
            <div className={styles.takeKicker}>Joe&apos;s take</div>
            <p className={styles.takeText}>{take ?? fallbackTake(open)}</p>
          </div>
        </div>
        <div className={styles.stats}>
          <div className={styles.stat}>
            <div className={styles.statValue}>{open.length}</div>
            <div className={styles.statLabel}>{open.length === 1 ? 'pick waiting on you' : 'picks waiting on you'}</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{newOpen}</div>
            <div className={styles.statLabel}>new to your area</div>
          </div>
          <div className={styles.stat}>
            <div className={styles.statValue}>{reserve.length}</div>
            <div className={styles.statLabel}>other options, if you want different people</div>
          </div>
        </div>
        <div className={styles.takeFoot}>
          <span>
            How Joe chose: places {radius ? `within ${radius} mi ` : ''}in the categories on your Partner Profile, ranked by fit.
          </span>
          {fullNote && (
            <button type="button" className={styles.linkButton} aria-expanded={noteOpen} onClick={() => setNoteOpen((v) => !v)}>
              {noteOpen ? "Hide Joe's full note" : "Read Joe's full note"}
            </button>
          )}
        </div>
        {fullNote && noteOpen && <div className={styles.fullNote}>{fullNote}</div>}
      </section>

      {week.length === 0 ? (
        <div className={styles.note}>
          No picks are open this month. <Link to="/community">Open the Community page</Link>
        </div>
      ) : (
        <section className={styles.step} aria-label="Decide on your picks">
          <div className={styles.stepHead}>
            <div className={styles.stepTitleRow}>
              <span className={styles.stepNumber} aria-hidden="true">
                1
              </span>
              <div>
                <h3 className={styles.stepTitle}>Decide on your picks</h3>
                <p className={styles.stepSub}>Plan a touch, snooze for 30 days, or pass. Every choice teaches Joe what you like.</p>
              </div>
            </div>
            <div className={styles.progress}>
              <div className={styles.progressLabel}>
                {acted.length} of {week.length} decided
              </div>
              <progress className={styles.progressBar} max={100} value={decidedPct} aria-label="Picks decided" />
            </div>
          </div>

          {filters.length > 1 && (
            <div className={styles.chips} role="group" aria-label="Filter picks">
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={clsx(styles.chip, activeFilter === f.key && styles.chipActive)}
                  aria-pressed={activeFilter === f.key}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label} <span className={styles.chipCount}>{f.count}</span>
                </button>
              ))}
            </div>
          )}

          <div className={styles.list}>
            {open.length === 0 && <div className={styles.empty}>Every pick has a decision. Nice work.</div>}
            {shown.length > 0 && (
              <ul className={styles.rows}>
                {shown.map((pick) => (
                  <JoePickRow
                    key={pick.id}
                    pick={pick}
                    reasons={state?.vocabulary.reasons ?? []}
                    busy={busy}
                    onPlan={(p: Pick) => setTarget({ kind: 'pick', pick: p })}
                    onSnooze={(p) => snooze.mutate(p.id)}
                    onDismiss={(p, reason) => dismiss.mutate({ pickId: p.id, reason })}
                  />
                ))}
              </ul>
            )}
            {acted.length > 0 && (
              <>
                <div className={styles.decidedHead}>Decided · {acted.length}</div>
                <ul className={styles.rows}>
                  {acted.map((pick) => (
                    <li key={pick.id} className={styles.decidedRow}>
                      <span className={styles.avatarSmall} aria-hidden="true">
                        {pickInitials(pick.name)}
                      </span>
                      <span className={styles.decidedName}>{pick.name}</span>
                      <span className={clsx(styles.statePill, STATE_CLASS[pick.state])}>{STATE_LABEL[pick.state] ?? pick.state}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </section>
      )}

      <ReservePicks picks={reserve} busy={busy} onAdd={(p) => addReserve.mutate(p.id)} step={week.length > 0 ? 2 : undefined} />

      <div className={styles.note}>
        The same picks as the <Link to="/community">Community page</Link>: acting here or there shows in both.
      </div>

      {state && (
        <PlanTouchModal
          target={target}
          vocabulary={state.vocabulary}
          joeActive={state.joeActive && !state.joePaused}
          categories={state.settings.categoryOptions}
          busy={plan.isPending}
          onClose={() => setTarget(null)}
          onSave={(input) => plan.mutate(input)}
        />
      )}
    </div>
  );
}
